import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  setCurrentTab,
  setAhuTabAccordion,
  openRatingRequest,
} from 'app/slices/ahuBoardSlice';
import type {
  IAhuBoardSection,
  IAhuBoardTunnel,
  IAhuBoardUnit,
  ICmsAhuDoc,
  IConstructionClass,
} from 'models';
import TabAhu from './tabs/TabAhu';
import TagStrip from './tabs/TagStrip';
import TabSections from './tabs/TabSections';
import TabComponents from './tabs/TabComponents';
import TabAccessories from './tabs/TabAccessories';

/**
 * §7.2 — section types that expose the RATING/SELECTION navy button. Verbatim
 * legacy `hasRating` (tabs.navy.panel.js:3); `evap_pack` is its last entry and
 * was dropped in the port, so the button rendered `invisible-tab` for Evap Pack
 * sections and there was no way to reach the selector. _ui.nav.scss:376-384
 * styles `#show_rating[data-type=evap_pack]` into the SELECTION label, which is
 * the stylesheet's own record that this type is meant to render the button.
 */
const HAS_RATING = [
  'fan',
  'coil',
  'gas_heater',
  'plate_heat_exchanger',
  'energy_wheel',
  'er_wheel',
  'filter',
  'rotary',
  'evap_pack',
];

interface FormsPanelProps {
  ahu: IAhuBoardUnit | undefined;
  tunnels: IAhuBoardTunnel[];
  /** Flat section list (all tunnels). */
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  constructionClasses: IConstructionClass[] | undefined;
  mfgId: number;
  isPowerUser: boolean;
}

/**
 * #forms-main-container — ViewTabsContainer (§7.0, tabs.container.tmpl markup
 * faithful) + ViewTabsNavyPanel (§7.2). 6e1: the four tab BODIES are live —
 * AHU accordion (TabAhu), tag strip, per-section forms + bulk panel
 * (TabSections), components/accessories hosting shells (rows; forms = 6e2).
 * Exactly one body `.active`, switched by `selection.currentTab` (parity 98).
 *
 * [NEG]#38 honored: the hidden `.ahu-form-heading` AHU-Tag block is NOT
 * ported. Navy chains per §13: AHU click also opens the selected tunnel's
 * accordion (row 3); RATING resolves through the rating seam (rows 6/7).
 */
const FormsPanel = ({
  ahu,
  tunnels,
  sections,
  cms,
  constructionClasses,
  mfgId,
  isPowerUser,
}: FormsPanelProps) => {
  const dispatch = useAppDispatch();
  const currentTab = useAppSelector((s) => s.ahuBoard.selection.currentTab);
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const ratingActive = useAppSelector((s) => s.ahuBoard.boardMode.ratingActive);

  const currentSection = sections.find((s) => s.id === currentSectionId);
  const sectionType = (currentSection?.type ?? '').toLowerCase();
  const noSelection = !currentSection;
  const isPlenum =
    sectionType === 'plenum' || sectionType === 'split_plenum' || sectionType === 'split-plenum';
  const hideRating = !HAS_RATING.includes(sectionType);

  // §7.2 render hook (legacy tabs.navy.panel.js:116-120): plenum sections have
  // no COMPONENT tab — whenever one is current while that tab is active, fall
  // back to SECTION (showSection). Reuses the same `isPlenum` as the
  // non-clickable gating on #show_components below, so the two cannot drift.
  useEffect(() => {
    if (isPlenum && currentTab === 'components') dispatch(setCurrentTab('sections'));
  }, [isPlenum, currentTab, dispatch]);

  const containerClass = (tab: string) =>
    `forms-container${currentTab === tab ? ' active' : ''}`;

  const showAhu = () => {
    dispatch(setCurrentTab('ahu'));
    // §13 row 3 — open the selected (or first) tunnel's accordion.
    const tunnelId = currentSection?.tunnel_id ?? tunnels[0]?.id;
    if (tunnelId != null) dispatch(setAhuTabAccordion(`tunnel:${tunnelId}`));
  };

  const showRating = () => {
    if (hideRating || ahu?.static_version || currentSectionId == null) return;
    // §13 rows 6/7 — navy #show_rating. Legacy (tabs.navy.panel.js showRating)
    // resolves the HOST COMPONENT and clicks its form's rating label, so the
    // selector opens against the component id (current_model.id), not the AHU.
    //
    // React port: the component form (useComponentForm.openRatingStub) is the
    // single source of the full activate payload — it emits
    // `rating_mode:activate {name: partName, current_model:{id: componentId},
    // section}`. We MUST NOT emit a bare `{name, loading:true}` here: that races
    // the form's emit and wins (synchronous), building the dialog with
    // modelId === ahu.id (renderFromAHU fallback) → the selector queries the
    // AHU id → 400 → stuck preloader (#70 / W3 F1).
    //
    // Instead: (1) switch to the components tab so the host form MOUNTS and
    // registers its window.__FORM_SHIM__[id] (the selector reads the shim off
    // that id; the React PERF rule only mounts the active tab's form — legacy
    // kept it permanently in the DOM, which is why legacy could just click it);
    // (2) bump the rating request so the now-mounted form fires its own opener
    // with the resolved component id. This mirrors the legacy waitForRate →
    // section:rendered re-emit, with the form as the id authority.
    if (currentTab !== 'components') dispatch(setCurrentTab('components'));
    dispatch(openRatingRequest(currentSectionId));
  };

  return (
    <div id="forms-main-container" className="left-form-container conected-to-error-log">
      <div id="forms-navy-panel">
        <div className="form-ctrl-panel form-ctrl-section clearfix active">
          <button
            id="show_rating"
            data-type={sectionType}
            className={`right${hideRating ? ' invisible-tab' : ''}${
              ratingActive ? ' active' : ''
            }${ahu?.static_version ? ' non-clickable' : ''}`}
            tabIndex={-1}
            onClick={showRating}
          >
            <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24">
              <path d="m482-200 114-113-114-113-42 42 43 43q-28 1-54.5-9T381-381q-20-20-30.5-46T340-479q0-17 4.5-34t12.5-33l-44-44q-17 25-25 53t-8 57q0 38 15 75t44 66q29 29 65 43.5t74 15.5l-38 38 42 42Zm165-170q17-25 25-53t8-57q0-38-14.5-75.5T622-622q-29-29-65.5-43T482-679l38-39-42-42-114 113 114 113 42-42-44-44q27 0 55 10.5t48 30.5q20 20 30.5 46t10.5 52q0 17-4.5 34T603-414l44 44ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
            </svg>
            <span className="rating">RATING</span>
            <span className="filter-selection">SELECTION</span>
          </button>
          <button
            id="show_accessories"
            className={`right${currentTab === 'accessories' ? ' active' : ''}${
              noSelection ? ' non-clickable-tab' : ''
            }`}
            tabIndex={-1}
            onClick={() => {
              if (!noSelection) dispatch(setCurrentTab('accessories'));
            }}
          >
            ACCESSORIES
          </button>
          <button
            id="show_components"
            className={`right${currentTab === 'components' ? ' active' : ''}${
              noSelection ? ' non-clickable-tab' : ''
            }${isPlenum ? ' non-clickable' : ''}`}
            tabIndex={-1}
            onClick={() => {
              if (!noSelection && !isPlenum) dispatch(setCurrentTab('components'));
            }}
          >
            COMPONENT
          </button>
          <button
            id="show_sections"
            className={`right${currentTab === 'sections' ? ' active' : ''}${
              noSelection ? ' non-clickable-tab' : ''
            }`}
            tabIndex={-1}
            onClick={() => {
              if (!noSelection) dispatch(setCurrentTab('sections'));
            }}
          >
            SECTION
          </button>
          <button
            id="show_ahu"
            className={`right ahu-parameters${currentTab === 'ahu' ? ' active' : ''}`}
            tabIndex={-1}
            onClick={showAhu}
          >
            AHU
          </button>
        </div>
      </div>

      <div id="forms_ahu" className={containerClass('ahu')}>
        {ahu && (
          <TabAhu
            ahu={ahu}
            tunnels={tunnels}
            sections={sections}
            cms={cms}
            constructionClasses={constructionClasses}
            mfgId={mfgId}
            isPowerUser={isPowerUser}
          />
        )}
      </div>
      <div className="tag_wrap">
        {currentSection && currentTab !== 'ahu' && (
          <TagStrip section={currentSection} cms={cms} ahuId={ahu?.id} />
        )}
      </div>
      <div id="forms_sections" className={containerClass('sections')}>
        {ahu && (
          <TabSections
            ahu={ahu}
            tunnels={tunnels}
            sections={sections}
            cms={cms}
            mfgId={mfgId}
          />
        )}
      </div>
      <div id="forms_components" className={containerClass('components')}>
        <TabComponents sections={sections} cms={cms} />
      </div>
      <div id="forms_accessories" className={containerClass('accessories')}>
        {ahu && (
          <TabAccessories ahuId={ahu.id} sections={sections} tunnels={tunnels} cms={cms} />
        )}
      </div>
    </div>
  );
};

export default FormsPanel;
